const EPS = 1e-6

function segmentIntersection(a1, a2, b1, b2) {
  const d1x = a2.x - a1.x
  const d1y = a2.y - a1.y
  const d2x = b2.x - b1.x
  const d2y = b2.y - b1.y
  const denom = d1x * d2y - d1y * d2x
  if (Math.abs(denom) < 1e-9) return null
  const t = ((b1.x - a1.x) * d2y - (b1.y - a1.y) * d2x) / denom
  const u = ((b1.x - a1.x) * d1y - (b1.y - a1.y) * d1x) / denom
  if (t < -EPS || t > 1 + EPS || u < -EPS || u > 1 + EPS) return null
  return { x: a1.x + t * d1x, y: a1.y + t * d1y, t, u }
}

function nodeKey(x, y) {
  return `${Math.round(x / EPS)}:${Math.round(y / EPS)}`
}

// Splits every beam at every point where another beam crosses or touches it (shared endpoints,
// T-junctions, and mid-span crossings alike), then builds an undirected planar graph over the
// resulting sub-segments: nodes are the distinct split points, edges connect consecutive points
// along each original beam. Every edge in this graph is therefore a real piece of a real beam, so
// any face traced from it has boundaries that line up exactly with beam positions.
export function buildBeamPlanarGraph(beams) {
  const breakPointsByBeam = beams.map((b) => [
    { x: b.xI, y: b.yI, t: 0 },
    { x: b.xJ, y: b.yJ, t: 1 },
  ])

  for (let i = 0; i < beams.length; i++) {
    for (let j = i + 1; j < beams.length; j++) {
      const a = beams[i]
      const b = beams[j]
      const inter = segmentIntersection(
        { x: a.xI, y: a.yI },
        { x: a.xJ, y: a.yJ },
        { x: b.xI, y: b.yI },
        { x: b.xJ, y: b.yJ }
      )
      if (inter) {
        breakPointsByBeam[i].push({ x: inter.x, y: inter.y, t: inter.t })
        breakPointsByBeam[j].push({ x: inter.x, y: inter.y, t: inter.u })
      }
    }
  }

  const nodeIndex = new Map()
  const nodes = []
  function getNodeId(x, y) {
    const k = nodeKey(x, y)
    let id = nodeIndex.get(k)
    if (id === undefined) {
      id = nodes.length
      nodes.push({ x, y })
      nodeIndex.set(k, id)
    }
    return id
  }

  const adjacency = new Map()
  function addAdj(u, v) {
    if (!adjacency.has(u)) adjacency.set(u, [])
    if (!adjacency.get(u).includes(v)) adjacency.get(u).push(v)
  }

  beams.forEach((beam, i) => {
    const pts = [...breakPointsByBeam[i]].sort((p1, p2) => p1.t - p2.t)
    const uniq = []
    pts.forEach((p) => {
      const last = uniq[uniq.length - 1]
      if (!last || Math.hypot(p.x - last.x, p.y - last.y) > EPS) uniq.push(p)
    })
    for (let k = 0; k < uniq.length - 1; k++) {
      const u = getNodeId(uniq[k].x, uniq[k].y)
      const v = getNodeId(uniq[k + 1].x, uniq[k + 1].y)
      if (u === v) continue
      addAdj(u, v)
      addAdj(v, u)
    }
  })

  return { nodes, adjacency }
}

// Traces every face of the planar graph with the standard "next = neighbor right after the
// reverse-incoming direction, in angular order" walk: applying it to every directed edge exactly
// once partitions all of them into cycles, one per face — including the single unbounded outer
// face. Bounded faces come out with one winding order and the outer face with the opposite one
// (see findBeamFaceAt), which is how callers tell them apart.
function traceFaces(nodes, adjacency) {
  const sortedNeighbors = new Map()
  adjacency.forEach((neighbors, u) => {
    const withAngles = neighbors.map((v) => ({
      v,
      angle: Math.atan2(nodes[v].y - nodes[u].y, nodes[v].x - nodes[u].x),
    }))
    withAngles.sort((a, b) => a.angle - b.angle)
    sortedNeighbors.set(u, withAngles)
  })

  function nextEdge(u, v) {
    const list = sortedNeighbors.get(v)
    const idx = list.findIndex((entry) => entry.v === u)
    return list[(idx + 1) % list.length].v
  }

  const visited = new Set()
  const faces = []

  adjacency.forEach((neighbors, u) => {
    neighbors.forEach((v) => {
      if (visited.has(`${u}->${v}`)) return
      const face = []
      let curU = u
      let curV = v
      for (;;) {
        visited.add(`${curU}->${curV}`)
        face.push(curU)
        const next = nextEdge(curU, curV)
        curU = curV
        curV = next
        if (curU === u && curV === v) break
        if (face.length > adjacency.size + 5) break // safety valve against malformed graphs
      }
      faces.push(face)
    })
  })

  return faces
}

function shoelaceArea(polygon) {
  let sum = 0
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i]
    const b = polygon[(i + 1) % polygon.length]
    sum += a.x * b.y - b.x * a.y
  }
  return sum / 2
}

function pointInPolygonRayCast(point, polygon) {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x
    const yi = polygon[i].y
    const xj = polygon[j].x
    const yj = polygon[j].y
    const intersect = yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

// Finds the smallest closed region bounded entirely by beams that contains `point` — the "click
// inside a closed loop of beams to create a slab" flow. The resulting polygon's vertices are beam
// endpoints/intersections and its edges are beam sub-segments, so every slab edge lines up with a
// real beam by construction. Returns null when no beam loop encloses the point (open structure,
// cantilever, click outside all beams, etc.), so callers can fall back to manual polygon drawing.
export function findBeamFaceAt(beams, point) {
  if (beams.length < 3) return null
  const { nodes, adjacency } = buildBeamPlanarGraph(beams)
  const faces = traceFaces(nodes, adjacency)

  // traceFaces's walk yields bounded faces with a negative shoelace area and the single unbounded
  // outer face with a positive one (their vertex order runs opposite to the interior faces') — so
  // a negative area is what tells a real cell apart from the outer boundary.
  let best = null
  let bestArea = Infinity
  faces.forEach((face) => {
    if (face.length < 3) return
    const polygon = face.map((id) => nodes[id])
    const area = shoelaceArea(polygon)
    if (area >= -EPS) return // discard the outer face / degenerate zero-area traces
    if (!pointInPolygonRayCast(point, polygon)) return
    const absArea = -area
    if (absArea < bestArea) {
      bestArea = absArea
      best = polygon
    }
  })

  return best
}
